import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { FundingPageShell } from '../../components/smart/FundingPageShell';
import { Button } from '../../components/smart/ui/Button';
import { cn } from '../../components/smart/utils/cn';
import { formatCurrency } from '../../components/smart/utils/format';
import { stateName } from '../../components/smart/utils/states';
import { SMART_ROUTES } from '../../constants';
import {
  smartMockMerchants,
  smartMockOperations,
  smartMockPartner,
} from '../../gallery/smartMock';

export const MerchantPickerPage = () => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();
  const [stateFilter, setStateFilter] = useState('');
  const operation = smartMockOperations.find(
    (o) => o.fundingStatus === 'awaiting_funding',
  )!;

  const stateOptions = useMemo(() => {
    const codes = new Set<string>();
    smartMockMerchants.forEach((m) => m.states.forEach((s) => codes.add(s)));
    return [...codes]
      .map((code) => ({ code, name: stateName('US', code) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const visibleMerchants = useMemo(() => {
    const list = stateFilter
      ? smartMockMerchants.filter((m) => m.states.includes(stateFilter))
      : smartMockMerchants;
    return [...list].sort((a, b) => a.fee - b.fee);
  }, [stateFilter]);

  return (
    <FundingPageShell linkId={smartMockPartner.linkId}>
        <section className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('cashDeposit.title')}
          </h1>
          <p className="text-fg-muted text-sm">{t('cashDeposit.subtitle')}</p>
        </section>

        <div className="border-border bg-bg-elevated mt-5 flex items-center justify-between rounded-2xl border p-4">
          <span className="text-fg-muted text-[11px] font-semibold uppercase tracking-wide">
            {t('cashDeposit.amountLabel')}
          </span>
          <span className="text-fg text-xl font-semibold tabular-nums">
            {formatCurrency(operation.sendAmount, operation.sourceCurrency)}
          </span>
        </div>

        {stateOptions.length > 0 && (
          <label className="mt-5 flex flex-col gap-1.5">
            <span className="text-fg-muted text-[11px] font-semibold uppercase tracking-wide">
              {t('cashDeposit.stateLabel')}
            </span>
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="border-border bg-bg text-fg focus:ring-accent appearance-none rounded-xl border px-4 py-3 text-base focus:outline-none focus:ring-2"
            >
              <option value="">{t('cashDeposit.allStates')}</option>
              {stateOptions.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="mt-5 flex flex-col gap-1.5" role="radiogroup">
          <span className="text-fg-muted text-[11px] font-semibold uppercase tracking-wide">
            {t('cashDeposit.merchantsLabel')}
          </span>
          <div className="grid grid-cols-1 gap-2">
            {visibleMerchants.map((m) => (
              <button
                key={m.merchantId}
                type="button"
                role="radio"
                aria-checked={false}
                onClick={() => navigate(SMART_ROUTES.BARCODE)}
                className={cn(
                  'border-border bg-bg-elevated text-fg flex items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-medium transition-all',
                  'hover:border-fg-muted',
                )}
              >
                <span className="bg-bg-muted text-fg-muted flex size-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold">
                  {m.name.charAt(0)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold">{m.name}</span>
                  <span className="text-fg-muted mt-0.5 text-[11px]">
                    {m.fee > 0
                      ? t('cashDeposit.feeValue', {
                          fee: formatCurrency(m.fee, 'USD'),
                        })
                      : t('cashDeposit.feeFree')}
                  </span>
                </span>
              </button>
            ))}
          </div>
          <p className="text-fg-muted mt-1 text-[11px]">
            {t('cashDeposit.tapHint')}
          </p>
        </div>

        <div className="mt-4">
          <Button
            variant="ghost"
            fullWidth
            onClick={() => navigate(SMART_ROUTES.HOME)}
          >
            {t('cashDeposit.backHome')}
          </Button>
        </div>
    </FundingPageShell>
  );
};
